import type { Ref } from 'vue'
import type { CtaEntryPoint, FlowStep } from '@/features/crosschain-buy/domain/modal'
import { getBuyFlowController } from '@/features/crosschain-buy/buy-flow/controller'
import type { ModalInteractionHooks } from '@/features/crosschain-buy/buy-flow/contracts'

export interface UseCrosschainModalLifecycleDeps {
  isQuoteFresh: Ref<boolean>
  hasQuotePrerequisites: () => boolean
  triggerRequote: () => void | Promise<void>
  debouncedAutoQuote: () => void
  syncQuoteForCurrentSelection: () => void
  syncTonWalletFromSession: () => void
  trackBuySpotty: (entryPoint: CtaEntryPoint) => void
}

export interface UseCrosschainModalLifecycleResult {
  closeModal: () => void
  openFromCta: (entryPoint: CtaEntryPoint, signal?: AbortSignal) => Promise<void>
  setStep: (step: FlowStep) => void
  markBridgeCompleted: (payload: { txHash: string; usdtAmount: string }) => Promise<void>
  resetForNewSwap: () => void
  invalidateRouteContext: (immediate?: boolean) => void
  runContinue: () => Promise<void>
  getModalPresentation: (viewportWidth: number) => 'mobile_fullscreen' | 'desktop_centered'
}

export function useCrosschainModalLifecycle(
  deps: UseCrosschainModalLifecycleDeps
): UseCrosschainModalLifecycleResult {
  const hooks: ModalInteractionHooks = {
    isQuoteFresh: () => deps.isQuoteFresh.value,
    hasQuotePrerequisites: deps.hasQuotePrerequisites,
    triggerRequote: deps.triggerRequote,
    debouncedAutoQuote: deps.debouncedAutoQuote,
    syncQuoteForCurrentSelection: deps.syncQuoteForCurrentSelection,
    syncTonWalletFromSession: deps.syncTonWalletFromSession,
    trackBuySpotty: deps.trackBuySpotty,
  }

  const controller = getBuyFlowController()

  return {
    closeModal: () => {
      controller.modal.close()
    },
    openFromCta: (entryPoint, signal) => controller.modal.open(entryPoint, hooks, signal),
    setStep: (step) => {
      controller.modal.setStep(step)
    },
    markBridgeCompleted: (payload) => controller.modal.markBridgeCompleted(payload),
    resetForNewSwap: () => {
      controller.modal.resetForNewSwap()
    },
    invalidateRouteContext: (immediate) => {
      controller.modal.invalidateRouteContext(hooks, immediate)
    },
    runContinue: () => controller.modal.runContinue(hooks),
    getModalPresentation: controller.modal.getPresentation,
  }
}
